import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  X,
  Globe,
  Package,
  Calendar,
  Code,
  Zap,
  Shield,
  CheckCircle,
  ExternalLink
} from 'lucide-react';

interface AboutModalProps {
  onClose: () => void;
}

export default function AboutModal({ onClose }: AboutModalProps) {
  const APP_VERSION = '2.1.420';
  const BUILD_DATE = 'October 12, 2026';
  const BUILD_NUMBER = '20261012.2047';

  const features = [
    'Mobile-first iOS design',
    'Multi-tenant SaaS architecture',
    'Enterprise sync system',
    'Offline-first capability',
    'Real-time collaboration',
    'Advanced analytics',
    'Role-based access control',
    'White-label branding',
  ];

  const techStack = [
    { name: 'React', version: '18+' },
    { name: 'TypeScript', version: 'Latest' },
    { name: 'Tailwind CSS', version: '4.0' },
    { name: 'Supabase', version: 'Latest' },
    { name: 'IndexedDB', version: 'Native' },
    { name: 'Recharts', version: 'Latest' },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl">OilWellX</CardTitle>
                <p className="text-sm text-gray-500">Production Management Platform</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          {/* Version Info */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Package className="h-5 w-5 text-blue-600" />
              <h3 className="font-medium">Version Information</h3>
            </div>
            <div className="space-y-2 pl-7">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Version</span>
                <Badge variant="outline" className="font-mono">v{APP_VERSION}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Build Date</span>
                <span className="text-sm font-medium">{BUILD_DATE}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Build Number</span>
                <span className="text-sm font-mono text-gray-500">{BUILD_NUMBER}</span>
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="h-5 w-5 text-purple-600" />
              <h3 className="font-medium">Key Features</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 pl-7">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {feature}
                </div>
              ))}
            </div>
          </div>

          {/* Tech Stack */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Code className="h-5 w-5 text-orange-600" />
              <h3 className="font-medium">Technology Stack</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 pl-7">
              {techStack.map((tech, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">{tech.name}</span>
                  <span className="text-xs text-gray-500">{tech.version}</span>
                </div>
              ))}
            </div>
          </div>

          {/* System Status */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="h-5 w-5 text-green-600" />
              <h3 className="font-medium">System Status</h3>
            </div>
            <div className="space-y-2 pl-7">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">All Systems Operational</span>
                </div>
                <Badge className="bg-green-600">Online</Badge>
              </div>
            </div>
          </div>

          {/* Links */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ExternalLink className="h-5 w-5 text-blue-600" />
              <h3 className="font-medium">Resources</h3>
            </div>
            <div className="space-y-2 pl-7">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => window.open('https://docs.oilwellx.com', '_blank')}
              >
                <Globe className="h-4 w-4 mr-2" />
                Documentation
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => window.open('https://support.oilwellx.com', '_blank')}
              >
                <Shield className="h-4 w-4 mr-2" />
                Support Center
              </Button>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-4 border-t text-center text-xs text-gray-500">
            <p>© {new Date().getFullYear()} OilWellX. All rights reserved.</p>
            <p className="mt-1">Built with React, TypeScript, and Tailwind CSS</p>
            <p className="mt-1">Powered by Supabase & Figma Make</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
































































































































































































































































































































































































































